import { useEffect, useState } from 'react';

import { cn } from '../../utils/cn';

type Props = {
    ariaLabel: string;
    max: number;
    onChange: (value: number) => void;
    value: number;
};

const pad = (value: number) => value.toString().padStart(2, '0');

const parseSegment = (text: string, max: number): number | null => {
    if (!text) return null;
    const parsed = parseInt(text, 10);
    return parsed <= max ? parsed : null;
};

export const TimeSegmentInput: React.FC<Props> = ({ ariaLabel, max, onChange, value }) => {
    const [text, setText] = useState(pad(value));

    useEffect(() => {
        setText(pad(value));
    }, [value]);

    const isInvalid = text.length === 2 && parseSegment(text, max) === null;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const digits = event.target.value.replace(/\D/g, '').slice(0, 2);
        setText(digits);
        // commit only complete segments, otherwise the re-padding would interrupt typing
        const parsed = digits.length === 2 ? parseSegment(digits, max) : null;
        if (parsed !== null) onChange(parsed);
    };

    const handleBlur = () => {
        const parsed = parseSegment(text, max);
        if (parsed === null) {
            setText(pad(value));
            return;
        }
        setText(pad(parsed));
        onChange(parsed);
    };

    return (
        <input
            type="text"
            inputMode="numeric"
            aria-label={ariaLabel}
            aria-invalid={isInvalid}
            value={text}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={(event) => event.target.select()}
            className={cn(
                'h-8 w-[70px] rounded border bg-white text-center text-sm text-midnight',
                isInvalid ? 'border-alert-error' : 'border-grey-dark',
                'focus:outline-none focus:ring-2 focus:ring-blue-focus focus:ring-offset-1',
            )}
        />
    );
};
